import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Download, Megaphone, RefreshCw, Sparkles, X } from 'lucide-react';
import { API_BASE_URL } from '../apiBase';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from './ui/dialog';
import OfficeModalHeader from './office/OfficeModalHeader';
import {
  dismissAnnouncement,
  getAppliedVersion,
  getDismissedAnnouncements,
  hardReloadApp,
  markVersionApplied,
} from '../utils/hardReload';
import { parseReleaseForDisplay, resolveReleaseBody } from '../lib/releaseNotesFormat';

const API_URL = API_BASE_URL;
const POLL_MS = 2 * 60 * 1000;

function resolveImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

async function reportAck(announcementId, event, version) {
  if (!announcementId) return;
  try {
    await axios.post(
      `${API_URL}/api/app/updates/ack`,
      { announcement_id: announcementId, event, version: version || undefined },
      { withCredentials: true },
    );
  } catch {
    /* optional */
  }
}

function ReleaseNotesBody({ announcement, locale, fallback }) {
  const raw = resolveReleaseBody(announcement, locale) || fallback || '';
  const { intro, sections } = useMemo(
    () => parseReleaseForDisplay(raw, locale),
    [raw, locale],
  );

  if (!raw.trim()) {
    return (
      <p className="text-sm leading-relaxed text-muted-foreground">{fallback}</p>
    );
  }

  if (!sections.length) {
    return (
      <div className="max-h-56 overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
        {raw}
      </div>
    );
  }

  return (
    <div className="max-h-56 space-y-3 overflow-y-auto pr-1">
      {intro ? (
        <p className="text-[13px] leading-relaxed text-muted-foreground">{intro}</p>
      ) : null}
      {sections.map((sec) => (
        <div key={`${sec.emoji}-${sec.title}`} className="space-y-1.5">
          <p className="text-[13px] font-semibold tracking-tight text-foreground">
            <span className="mr-1.5" aria-hidden>
              {sec.emoji}
            </span>
            {sec.title}
          </p>
          <ul className="space-y-1.5 border-l border-border/80 pl-3">
            {sec.items.map((item) => (
              <li key={item} className="text-[12.5px] leading-snug text-muted-foreground">
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function AppUpdateModal({ userId = "anonymous" }) {
  const deferredRef = useRef(new Set());
  const deferKey = `aurax_deferred_version:${userId}`;
  const { t, locale } = useLanguage();
  const [versionModal, setVersionModal] = useState(null);
  const [announcementModal, setAnnouncementModal] = useState(null);
  const [reloading, setReloading] = useState(false);
  const updateRequestRef = useRef({ active: false, lastAt: null });

  const evaluate = useCallback((data) => {
    if (!data) return;
    const dismissed = getDismissedAnnouncements();

    const versionUpdate = data.version_update;
    let deferred = deferredRef.current.has(`${deferKey}:${versionUpdate?.version}`);
    try { deferred ||= sessionStorage.getItem(deferKey) === versionUpdate?.version; } catch { /* memory fallback */ }
    if (versionUpdate?.version && versionUpdate.version !== getAppliedVersion()
        && (versionUpdate.force_reload === true || !deferred)) {
      setVersionModal((previous) => previous?.version === versionUpdate.version ? previous : versionUpdate);
      setAnnouncementModal(null);
      return;
    }

    const announcement = data.announcement;
    if (announcement && announcement.id && !dismissed.includes(announcement.id)) {
      // O render já prioriza o modal de versão. Não depender desse estado aqui:
      // isso recriava fetchUpdates e reiniciava o efeito após cada resposta.
      setAnnouncementModal((previous) => previous?.id === announcement.id ? previous : announcement);
    }
  }, [deferKey]);

  const fetchUpdates = useCallback(async () => {
    const request = updateRequestRef.current;
    if (document.visibilityState === 'hidden' || request.active ||
        (request.lastAt != null && Date.now() - request.lastAt < POLL_MS)) return;
    request.active = true;
    request.lastAt = Date.now();
    try {
      const { data } = await axios.get(`${API_URL}/api/app/updates`, { withCredentials: true });
      evaluate(data);
    } catch {
      /* optional */
    } finally {
      request.active = false;
    }
  }, [evaluate]);

  useEffect(() => {
    fetchUpdates();
    const timer = setInterval(fetchUpdates, POLL_MS);
    const onFocus = () => fetchUpdates();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchUpdates]);

  useEffect(() => {
    if (announcementModal?.id) reportAck(announcementModal.id, 'seen');
  }, [announcementModal?.id]);

  useEffect(() => {
    if (versionModal?.id) reportAck(versionModal.id, 'seen', versionModal.version);
  }, [versionModal?.id, versionModal?.version]);

  const handleReload = async (version) => {
    setReloading(true);
    markVersionApplied(version || '');
    await reportAck(versionModal?.id, 'updated', version);
    await hardReloadApp(version);
  };

  const handleDismissAnnouncement = () => {
    if (announcementModal?.id) {
      dismissAnnouncement(announcementModal.id);
      reportAck(announcementModal.id, 'dismissed');
    }
    setAnnouncementModal(null);
  };

  const handleDeferVersion = () => {
    if (versionModal?.force_reload === true) return;
    if (versionModal?.version) {
      deferredRef.current.add(`${deferKey}:${versionModal.version}`);
      try { sessionStorage.setItem(deferKey, versionModal.version); } catch { /* memory fallback */ }
    }
    if (versionModal?.id) reportAck(versionModal.id, 'deferred', versionModal.version);
    setVersionModal(null);
  };

  const blocking = versionModal?.force_reload === true;
  const versionTitle = versionModal
    ? (versionModal.title && !/^Office\s/i.test(versionModal.title)
        ? versionModal.title
        : t('update.versionTitle', { version: versionModal.version }))
    : '';

  return (
    <>
      <Dialog open={!!versionModal} onOpenChange={(open) => { if (!open && !blocking) handleDeferVersion(); }}>
        <DialogContent className="sm:max-w-lg border-border bg-card text-foreground" onPointerDownOutside={(e) => blocking && e.preventDefault()} onEscapeKeyDown={(e) => blocking && e.preventDefault()}>
          {versionModal && (
            <>
              <OfficeModalHeader
                layout="row"
                variant="circle"
                icon={Sparkles}
                title={versionTitle}
                description={
                  blocking
                    ? t('update.versionDescForce', { version: versionModal.version })
                    : t('update.versionDesc', { version: versionModal.version })
                }
              />
              {versionModal.image_url && (
                <img
                  src={resolveImageUrl(versionModal.image_url)}
                  alt=""
                  className="max-h-56 w-full rounded-lg border border-border object-cover"
                />
              )}
              <ReleaseNotesBody
                announcement={versionModal}
                locale={locale}
                fallback={t('update.fallbackBody')}
              />
              <DialogFooter className="flex-col gap-2 sm:flex-row">
                {!blocking && (
                  <Button type="button" variant="outline" onClick={handleDeferVersion}>
                    {t('update.later')}
                  </Button>
                )}
                <Button
                  type="button"
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={reloading}
                  onClick={() => handleReload(versionModal.version)}
                >
                  {reloading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  {t('update.reloadNow')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!announcementModal && !versionModal} onOpenChange={(open) => !open && handleDismissAnnouncement()}>
        <DialogContent className="sm:max-w-lg border-border bg-card text-foreground">
          {announcementModal && (
            <>
              <OfficeModalHeader
                layout="row"
                variant="circle"
                icon={Megaphone}
                title={announcementModal.title}
                description={t('update.announcementDesc')}
              />
              {announcementModal.image_url && (
                <img
                  src={resolveImageUrl(announcementModal.image_url)}
                  alt=""
                  className="max-h-56 w-full rounded-lg border border-border object-cover"
                />
              )}
              <ReleaseNotesBody
                announcement={announcementModal}
                locale={locale}
                fallback=""
              />
              <DialogFooter>
                <Button type="button" className="w-full sm:w-auto" onClick={handleDismissAnnouncement}>
                  <X className="mr-2 h-4 w-4" />
                  {t('update.gotIt')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
